/* commin imports */

import { ObjectId } from "bson";
import { useRouter } from "next/router";
import React, { useState } from "react";
/* mui imports */

import { LocalPhoneOutlined } from "@mui/icons-material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
	Alert,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Link,
	Slide,
	Snackbar,
	styled,
	Tab,
	Tabs,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";

/* models import */

import { BusinessProfileInterface, PropertyInterface, UserInterface } from "../../../models";

/* redux imports */

import StateInterface from "../../../redux-magic/state-interface";
import transaction_type from "../../../redux-magic/sub-state-data-empty/transaction-tabs";
import { StoreLeadsThunk } from "../../../redux-magic/thunks";

/* components imports */

import { createLoginFlow, LoginFlowTriggers } from "../../../lib/auth/helpers";
import BeegruButton from "../../common-components/buttons/BeegruButton";

/* styled components */

const CustomDialog = styled(Dialog)(({ theme }) => ({
	"& .MuiDialog-paper": {
		[theme.breakpoints.down("sm")]: {
			borderRadius: "0rem",
			minHeight: "100%",
		},
		minHeight: "calc(100vh - 100px)",
		maxHeight: "calc(100vh - 100px)",
		borderRadius: "1rem",
		background: theme.palette.background.paper,
	},
}));

const CustomDialogTitle = styled(DialogTitle)(({ theme }) => ({
	display: "flex",
	flsexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	padding: "0rem 1rem",
}));

const GridContainer = styled("div")(({ theme }) => ({
	display: "grid",
	justifyContent: "center",
	alignItems: "center",
	gridTemplateColumns: "repeat(2, 1fr)",
	gap: "10px",
}));

const ContentContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	height: "100%",
}));

const Images = styled("img")(({ theme }) => ({
	width: "100%",
	height: "auto",
	maxHeight: "calc(100vh - 200px)",
	objectFit: "contain",
	cursor: "pointer",
}));

const Snack = styled(Snackbar)(({ theme }) => ({}));

const BottomArrowContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	position: "absolute",
	bottom: "20px",
	left: "50%",
	transform: "translateX(-50%)",
	gap: "0.5rem",
	background: "transparent",
	backgroundColor: "rgba(63, 55, 55, 0.5)",
	borderRadius: "1rem",
	padding: "0.25rem 0.5rem",
	width: "15rem",
	height: "3rem",
	[theme.breakpoints.down("sm")]: {
		width: "18.75rem",
		padding: "0.25rem 0.5rem",
		backgroundColor: "rgba(63, 55, 55, 0.5)",
		height: "auto",
		gap: "0.25rem",
	},
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
	width: "fit-content",
	alignSelf: "start",
	borderRadius: "8px",
	"& .MuiTabs-root > .MuiTabs-scroller > .MuiTabs-indicator": {
		display: "none",
	},
	minHeight: "0rem",
	height: "auto",
})) as typeof Tabs;

const StyledTab = styled(Tab)(({ theme, label }) => ({
	textTransform: "none",
	fontSize: "0.875rem",
	fontWeight: 500,
	gap: "0.5rem",
	letterSpacing: "0.025rem",
	background: "transparent",
	"&.Mui-selected": {
		background: " rgba(252, 128, 25, 0.16)",
		color: "#B05911",
		borderRadius: "1rem",
		border: "2px solid #B05911",
	},
})) as typeof Tab;

const TextOverlayContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "center",
	position: "absolute",
	bottom: "2px",
	right: "0px",
	zIndex: 2,
	backgroundColor: "rgba(0, 0, 0, 0.5)",
	padding: "5px 10px",
	// borderRadius: "1rem 0rem 0rem 0rem",
	// border: "1px solid #FFFFFF",
	cursor: "pointer",
	gap: "0.5rem",
	"&:hover": {
		opacity: 0.9,
	},
}));

const ButtonsContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	gap: "0.25rem",
	padding: "0rem 0.25rem 0rem 0.25rem",
}));

/* Dialog Transition */

const DialogTransition = React.forwardRef(function Transition(
	props: TransitionProps & {
		children: React.ReactElement<any, any>;
	},
	ref: React.Ref<unknown>,
) {
	return (
		<Slide
			direction="up"
			ref={ref}
			{...props}
		/>
	);
});

/* types */

type Category = "all" | "exterior" | "interior" | "master_plan" | "amenities" | "route_map";

/* interface */
interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

const ViewPropertyImagesModal = ({
	open,
	allImages,
	allImageUrlsWithCategory,
	handleClose,
	propertyCreatedBy,
	showContactDetails,
	session,
	propertyTitle,
	propertySlug,
	dispatch,
	isPropertyPublished,
	isEditor,
	profile_context,
	boost_information,
	propertyId,
	propertyLocation,
	login_flow_global_state,
}: {
	open: boolean;
	allImages: Array<string>;
	allImageUrlsWithCategory: Record<Category, string[]>;
	handleClose: () => void;
	propertyCreatedBy: LocalPropertyInterface["created_by"];
	showContactDetails: boolean;
	session: StateInterface["redux_session"];
	propertyTitle: string;
	propertySlug: string;
	dispatch: Function;
	isPropertyPublished: boolean;
	isEditor: boolean;
	profile_context: StateInterface["profile_context"];
	boost_information: StateInterface["property"]["boost_information"];
	propertyId: string | ObjectId;
	propertyLocation: string;
	login_flow_global_state: StateInterface["login_flow_global_state"];
}) => {
	const router = React.useRef(useRouter()).current;

	const theme = useTheme();

	const [openSingleViewModal, setOpenSingleViewModal] = useState<boolean>(false);

	const [selectedTab, setSelectedTab] = useState<Category>("all");

	const [selectedImage, setSelectedImage] = useState<string | null>(null);

	const [openNumber, setOpenNumber] = React.useState(false);

	const [touchStartX, setTouchStartX] = useState<number | null>(null);

	const [touchEndX, setTouchEndX] = useState<number | null>(null);

	const [touchStartY, setTouchStartY] = useState<number | null>(null);

	const [touchEndY, setTouchEndY] = useState<number | null>(null);

	const [snackopen, setSnackOpen] = React.useState<boolean>(false);

	const [snackBarMessage, setSnackBarMessage] = React.useState<string>("");

	const EDGE_SWIPE_THRESHOLD = 50; // Pixels from the screen edge to detect edge swipes
	const SWIPE_DOWN_THRESHOLD = 50; // Minimum swipe distance to trigger modal close

	// const minSwipeDistance = 50;

	const smDevice = useMediaQuery(theme.breakpoints.down("sm"));

	const isDesktop = useMediaQuery(theme.breakpoints.down("md1190"));

	// const fallbackImage: string = "/images/uploads/property.jpg";

	const creator_id: ObjectId | string = propertyCreatedBy
		? propertyCreatedBy.is_business_profile
			? propertyCreatedBy.business_profile_id != ""
				? (propertyCreatedBy.business_profile_id as BusinessProfileInterface)._id
				: propertyCreatedBy.user_id != ""
					? (propertyCreatedBy.user_id as UserInterface)._id
					: ""
			: propertyCreatedBy.user_id != ""
				? (propertyCreatedBy.user_id as UserInterface)._id
				: ""
		: "";

	const profile_context_id: ObjectId | string = profile_context
		? profile_context.is_business_profile
			? profile_context.business_profile_id
			: profile_context.user_id
		: "";

	const creator_country_code: string = propertyCreatedBy
		? propertyCreatedBy.is_business_profile
			? propertyCreatedBy.business_profile_id !== ""
				? (propertyCreatedBy.business_profile_id as BusinessProfileInterface).phone.country_code
				: propertyCreatedBy.user_id !== ""
					? (propertyCreatedBy.user_id as UserInterface).country_code
					: ""
			: propertyCreatedBy.user_id !== ""
				? (propertyCreatedBy.user_id as UserInterface).country_code
				: ""
		: "";

	const creator_phone: string = propertyCreatedBy
		? propertyCreatedBy.is_business_profile
			? propertyCreatedBy.business_profile_id !== ""
				? (propertyCreatedBy.business_profile_id as BusinessProfileInterface).phone.phone_number
				: propertyCreatedBy.user_id !== ""
					? (propertyCreatedBy.user_id as UserInterface).country_code
					: ""
			: propertyCreatedBy.user_id !== ""
				? (propertyCreatedBy.user_id as UserInterface).country_code
				: ""
		: "";

	const localLang = {
		heading: router.locale === "ar-AE" ? "مدرج بواسطة" : router.locale === "kn-IN" ? "ಲಿಸ್ಟ್ ಮಾಡಿದವರು" : "Listed by",
		snackValue:
			router.locale === "ar-AE"
				? "سنتواصل معك قريبًا!"
				: router.locale === "kn-IN"
					? "ನಾವು ಶೀಘ್ರದಲ್ಲಿ ನಿಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸುತ್ತೇವೆ!"
					: "We'll get back to you soon!",
		getACallback:
			router.locale === "ar-AE"
				? "احصل على مكالمة رجعية"
				: router.locale === "kn-IN"
					? "ಕಾಲ್‌ಬ್ಯಾಕ್ ಪಡೆಯಿರಿ"
					: "Get a callback",
		callNow: router.locale === "ar-AE" ? "اتصل الآن" : router.locale === "kn-IN" ? "ಈಗ ಕರೆ ಮಾಡಿ" : "Call",
		viewNumber:
			router.locale === "ar-AE" ? "عرض الرقم" : router.locale === "kn-IN" ? "ಫೋನ್ ಸಂಖ್ಯೆಯನ್ನು ವೀಕ್ಷಿಸಿ" : "View Number",
		uploadImage:
			router.locale === "ar-AE"
				? "تحميل الصورة"
				: router.locale === "kn-IN"
					? "ಚಿತ್ರವನ್ನು ಅಪ್‌ಲೋಡ್ ಮಾಡಿ"
					: "Upload Image",
		mentionSnack:
			router.locale === "ar-AR"
				? "لا تنس أن تذكر Beegru عندما تتصل"
				: router.locale === "kn-IN"
					? "ನೀವು ಕಾಲ್ ಮಾಡಿದಾಗ Beegru ಅನ್ನು ಹೇಳಲು ಮರೆತಿರಬೇಡಿ"
					: "Mention Beegru during your call for smoother communication.",
		mentionSnackCallBack:
			router.locale === "ar-AR"
				? "لا تنس أن تذكر Beegru عندما تتلقى مكالمة"
				: router.locale === "kn-IN"
					? "ನೀವು ಕರೆ ಸ್ವೀಕರಿಸಿದಾಗ Beegru ಅನ್ನು ಹೇಳಲು ಮರೆತಿರಬೇಡಿ"
					: "Mention Beegru during your call for smoother communication.",
	};

	const handleImageClick = (image: string) => {
		setSelectedImage(image);
		setOpenSingleViewModal(true);
	};

	const handleNextImage = () => {
		const currentIndex = allImages.indexOf(selectedImage!);
		const nextIndex = (currentIndex + 1) % allImages.length;
		setSelectedImage(allImages[nextIndex]);
	};

	const handlePrevImage = () => {
		const currentIndex = allImages.indexOf(selectedImage!);
		const prevIndex = (currentIndex - 1 + allImages.length) % allImages.length;
		setSelectedImage(allImages[prevIndex]);
	};

	const submit = async (call_now: boolean) => {
		call_now ? null : setSnackOpen(true);
		const first_name = session ? (session.user ? (session.user.dbUser ? session.user.dbUser.first_name : "") : "") : "";
		const last_name = session ? (session.user ? (session.user.dbUser ? session.user.dbUser.last_name : "") : "") : "";
		const page_title = session
			? session.user
				? session.user.dbUser
					? session.user.dbUser.pages
						? session.user.dbUser.pages[0].page_title
						: ""
					: ""
				: ""
			: "";
		await dispatch(
			StoreLeadsThunk({
				name:
					page_title && page_title !== "" ? page_title : first_name && last_name ? first_name + " " + last_name : "",
				phone: {
					country_code: session
						? session.user
							? session.user.dbUser
								? session.user.dbUser.country_code
								: "+91"
							: "+91"
						: "+91",
					phone_number: session ? (session.user ? (session.user.dbUser ? session.user.dbUser.phone : "") : "") : "",
				},
				agreed_to_tc: true,
				source: "/properties/" + propertySlug,
				listing_slug: propertySlug,
				listing_id: propertyId,
				listing_type: transaction_type == "ofb" ? "ofb property" : "property",
				is_guest: session ? false : true,
				listing_title: propertyTitle,
				listing_owner: {
					is_business_profile: propertyCreatedBy.is_business_profile,
					user_id: propertyCreatedBy
						? propertyCreatedBy.is_business_profile
							? (propertyCreatedBy.user_id as string | ObjectId)
							: propertyCreatedBy.user_id !== ""
								? (propertyCreatedBy.user_id as UserInterface)._id
								: undefined
						: undefined,
					business_profile_id: propertyCreatedBy
						? propertyCreatedBy.is_business_profile
							? (propertyCreatedBy.business_profile_id as BusinessProfileInterface)._id
							: (propertyCreatedBy.business_profile_id as string | ObjectId)
						: undefined,
				},
				user_id: session ? (session.user ? (session.user.dbUser ? session.user.dbUser._id : "") : "") : "",
				is_lead_from_boost: boost_information ? boost_information.active : false,
				created_by: {
					is_business_profile: profile_context.is_business_profile,
					user_id: profile_context.user_id as string | ObjectId,
					business_profile_id: profile_context.business_profile_id as string | ObjectId,
				},
			}),
		);
	};

	const handleCloseSnack = () => {
		setSnackOpen(false);
	};

	const minSwipeDistance = 50; // Minimum swipe distance to trigger an action
	const edgeSwipeThreshold = 50; // Edge detection threshold

	function onTouchStart(e: React.TouchEvent) {
		setTouchEndX(null);
		setTouchStartX(e.targetTouches[0].clientX);

		setTouchEndY(null);
		setTouchStartY(e.targetTouches[0].clientY);
	}

	function onTouchMove(e: React.TouchEvent) {
		setTouchEndX(e.targetTouches[0].clientX);
		setTouchEndY(e.targetTouches[0].clientY);
	}

	function onTouchEnd() {
		if (touchStartX !== null && touchEndX !== null && touchStartY !== null && touchEndY !== null) {
			const xDistance = touchStartX - touchEndX;
			const yDistance = touchStartY - touchEndY;

			// const isSwipeDown = yDistance < -minSwipeDistance;
			const isEdgeSwipe = touchStartX < edgeSwipeThreshold || touchStartX > window.innerWidth - edgeSwipeThreshold;

			// Close modal if user swipes from the edge or swipes downward
			if (isEdgeSwipe) {
				openSingleViewModal ? setOpenSingleViewModal(false) : handleClose();
				return; // Exit to prevent further execution
			}

			// Handle left/right swipes for image navigation
			const isLeftSwipe = xDistance > minSwipeDistance;
			const isRightSwipe = xDistance < -minSwipeDistance;

			if (isLeftSwipe) {
				handleNextImage();
			}

			if (isRightSwipe) {
				handlePrevImage();
			}
		}
	}

	const signIn = () => {
		if ((login_flow_global_state?.ui.type || "redirect") === "modal") {
			if (session && session.authorized) {
				new Function(...(login_flow_global_state?.action.sign_in || ["session", "return"])).call(session);
			} else {
				const { startFlow, resetFlow }: LoginFlowTriggers = createLoginFlow({
					dispatch,
					trigger_source: "navbar-dropdown",
					flow_type: "modal",
					autofill: false,
					action: {
						callback_url:
							process.env.PRODUCTION_API_URL +
							"auth/end-session?sessionId=" +
							encodeURIComponent(session?.session_id || "") +
							"&callbackUrl=" +
							encodeURIComponent(router.asPath),
						sign_in: (session: any) => {
							submit(false);
						},
						sign_up: (session: any) => {},
						sign_out: () => {},
					},
				});
				startFlow();
			}
		} else {
		}
	};

	React.useEffect(() => {
		if (allImages.length === 1 && open) {
			setSelectedImage(allImages[0]);
			setOpenSingleViewModal(true);
			handleClose();
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allImages, open]);

	return (
		<React.Fragment>
			<Snack
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				open={snackopen}
				autoHideDuration={3000}
				onClose={handleCloseSnack}
			>
				<Alert
					onClose={handleCloseSnack}
					severity="success"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
					variant="filled"
				>
					{snackBarMessage}
				</Alert>
			</Snack>
			<CustomDialog
				fullScreen={smDevice}
				open={open}
				maxWidth="md"
				fullWidth
				TransitionComponent={DialogTransition}
			>
				<CustomDialogTitle
					sx={{
						padding: "1rem",
					}}
				>
					{!smDevice && (
						<StyledTabs
							value={selectedTab}
							onChange={(event, newValue) => setSelectedTab(newValue as Category)}
							variant="scrollable"
							scrollButtons="auto"
							TabIndicatorProps={{ style: { display: "none" } }}
						>
							<StyledTab
								label="All"
								value="all"
							/>
							{allImageUrlsWithCategory.exterior.length > 0 && (
								<StyledTab
									label="Exterior"
									value="exterior"
								/>
							)}
							{allImageUrlsWithCategory.interior.length > 0 && (
								<StyledTab
									label="Interior"
									value="interior"
								/>
							)}
							{allImageUrlsWithCategory.amenities.length > 0 && (
								<StyledTab
									label="Amenities"
									value="amenities"
								/>
							)}
							{allImageUrlsWithCategory.master_plan.length > 0 && (
								<StyledTab
									label="Masterplan"
									value="master_plan"
								/>
							)}
							{allImageUrlsWithCategory.route_map.length > 0 && (
								<StyledTab
									label="Route Map"
									value="route_map"
								/>
							)}
						</StyledTabs>
					)}
					{smDevice && (
						<Typography
							variant="body1"
							sx={{ color: "#000", fontWeight: 400, fontSize: "1rem" }}
						>
							Property Images
						</Typography>
					)}
					<IconButton
						edge="end"
						color="inherit"
						onClick={handleClose}
						aria-label="close"
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</CustomDialogTitle>
				<DialogContent
					onTouchStart={onTouchStart}
					onTouchMove={onTouchMove}
					onTouchEnd={onTouchEnd}
					sx={{ padding: "0rem 0.5rem", scrollbarWidth: "none" }}
				>
					{selectedTab === "all" && (
						<div
							style={{
								columnCount: smDevice ? 1 : 2,
								columnGap: "1rem",
							}}
						>
							{allImages.map((image, index) => {
								const category = Object.keys(allImageUrlsWithCategory).find((category) =>
									allImageUrlsWithCategory[category as Category].includes(image),
								);
								return (
									<div
										key={index}
										style={{
											breakInside: "avoid",
											marginBottom: "1rem",
											position: "relative", // needed for absolute child
											borderRadius: "8px",
											overflow: "hidden",
										}}
									>
										<Images
											src={image}
											alt={`Property Image ${index + 1}`}
											onClick={() => handleImageClick(image)}
											style={{ width: "100%", display: "block" }}
										/>

										<div
											style={{
												position: "absolute",
												bottom: "8px",
												right: "8px",
												background: "rgba(0, 0, 0, 0.6)",
												color: "#fff",
												padding: "4px 8px",
												borderRadius: "4px",
												fontSize: "0.875rem",
												fontWeight: 500,
												maxWidth: "calc(100% - 16px)",
												whiteSpace: "nowrap",
												overflow: "hidden",
												textOverflow: "ellipsis",
											}}
										>
											{category?.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())}
										</div>
									</div>
								);
							})}
						</div>
					)}
					{selectedTab === "exterior" && (
						<>
							{allImageUrlsWithCategory.exterior.length > 0 && (
								<div
									style={{
										columnCount: smDevice ? 1 : 2,
										columnGap: "1rem",
									}}
								>
									{allImageUrlsWithCategory.exterior.map((image, index) => (
										<div
											key={index}
											style={{ breakInside: "avoid", marginBottom: "1rem" }}
										>
											<Images
												src={image}
												alt={`Exterior Image ${index + 1}`}
												onClick={() => handleImageClick(image)}
												style={{ width: "100%", borderRadius: "8px", display: "block" }}
											/>
										</div>
									))}
								</div>
							)}
						</>
					)}
					{selectedTab === "interior" && (
						<>
							{allImageUrlsWithCategory.interior.length > 0 && (
								<div
									style={{
										columnCount: smDevice ? 1 : 2,
										columnGap: "1rem",
									}}
								>
									{allImageUrlsWithCategory.interior.map((image, index) => (
										<div
											key={index}
											style={{ breakInside: "avoid", marginBottom: "1rem" }}
										>
											<Images
												key={index}
												src={image}
												alt={`Interior Image ${index + 1}`}
												onClick={() => handleImageClick(image)}
												style={{ width: "100%", borderRadius: "8px", display: "block" }}
											/>
										</div>
									))}
								</div>
							)}
						</>
					)}
					{selectedTab === "amenities" && (
						<>
							{allImageUrlsWithCategory.amenities.length > 0 && (
								<div
									style={{
										columnCount: smDevice ? 1 : 2,
										columnGap: "1rem",
									}}
								>
									{allImageUrlsWithCategory.amenities.map((image, index) => (
										<div
											key={index}
											style={{ breakInside: "avoid", marginBottom: "1rem" }}
										>
											<Images
												key={index}
												src={image}
												alt={`Amenities Image ${index + 1}`}
												onClick={() => handleImageClick(image)}
												style={{ width: "100%", borderRadius: "8px", display: "block" }}
											/>
										</div>
									))}
								</div>
							)}
						</>
					)}
					{selectedTab === "master_plan" && (
						<>
							{allImageUrlsWithCategory.master_plan.length > 0 && (
								<div
									style={{
										columnCount: smDevice ? 1 : 2,
										columnGap: "1rem",
									}}
								>
									{allImageUrlsWithCategory.master_plan.map((image, index) => (
										<div
											key={index}
											style={{ breakInside: "avoid", marginBottom: "1rem" }}
										>
											<Images
												key={index}
												src={image}
												alt={`Masterplan Image ${index + 1}`}
												onClick={() => handleImageClick(image)}
												style={{ width: "100%", borderRadius: "8px", display: "block" }}
											/>
										</div>
									))}
								</div>
							)}
						</>
					)}
					{selectedTab === "route_map" && (
						<>
							{allImageUrlsWithCategory.route_map.length > 0 && (
								<div
									style={{
										columnCount: smDevice ? 1 : 2,
										columnGap: "1rem",
									}}
								>
									{allImageUrlsWithCategory.route_map.map((image, index) => (
										<div
											key={index}
											style={{ breakInside: "avoid", marginBottom: "1rem" }}
										>
											<Images
												key={index}
												src={image}
												alt={`Route Map Image ${index + 1}`}
												onClick={() => handleImageClick(image)}
												style={{ width: "100%", borderRadius: "8px", display: "block" }}
											/>
										</div>
									))}
								</div>
							)}
						</>
					)}
				</DialogContent>
				<DialogActions
					sx={{
						padding: "1rem",
						display: "flex",
						flexDirection: "row",
						justifyContent: smDevice ? "space-between" : "flex-end",
					}}
				>
					{isPropertyPublished ? (
						<>
							{profile_context_id === creator_id || isEditor ? null : (
								<>
									<BeegruButton
										variant={showContactDetails ? "outlined" : "contained"}
										flavor="success"
										onClick={(event: any) => {
											event.preventDefault();
											session !== null ? submit(false) : signIn();
											setSnackOpen(true), setSnackBarMessage(localLang.mentionSnackCallBack);
										}}
										sx={{
											display: "flex",
											width: smDevice ? "100%" : "auto",
											fontSize: "0.75rem",
											[theme.breakpoints.up(theme.breakpoints.values.xs + 78)]: {
												fontSize: "0.875rem",
											},
										}}
									>
										{localLang.getACallback}
									</BeegruButton>

									{/* Call mow */}
									{showContactDetails && (
										<BeegruButton
											flavor="success"
											variant="contained"
											startIcon={<LocalPhoneOutlined />}
											href={"tel:" + creator_country_code + " " + creator_phone}
											onClick={(event: any) => {
												session !== null ? submit(true) : null;
												if (!smDevice) {
													event.preventDefault();
													setOpenNumber(!openNumber);
												}
												setSnackOpen(true);
												setSnackBarMessage(localLang.mentionSnack);
											}}
											sx={{
												display: "flex",
												width: smDevice ? "100%" : "auto",
											}}
										>
											{openNumber
												? creator_country_code + " " + creator_phone
												: !smDevice
													? "View Number"
													: localLang.callNow}
										</BeegruButton>
									)}

									{/* WhatsApp */}
									{showContactDetails && (
										<div
											style={{
												background: "#24D366",
												borderRadius: "0.75rem",
												height: "2.5rem",
												width: "2.5rem",
												[theme.breakpoints.up(theme.breakpoints.values.xs + 78)]: {
													fontSize: "0.875rem",
												},
											}}
										>
											<Link
												href={`https://wa.me/${creator_phone.replace(" ", "").replace("+", "")}?text=${encodeURIComponent("Hi, I'm interested in your listing \"")}${encodeURIComponent(propertyTitle)}${'" in '}${encodeURIComponent(propertyLocation)}${encodeURIComponent(" that I found on Beegru\n\n")}${encodeURIComponent((process.env.PRODUCTION_URL || "") + "properties/" + propertySlug + "?utm_source=whatsapp&utm_source_id=bottom_nav_whatsapp_button")}${encodeURIComponent("\n\nIs it still available?")}`}
												rel="noopener"
												target="_self"
												referrerPolicy="no-referrer"
												aria-label="Share via Whatsapp"
												sx={{
													textDecoration: "none",
													color: "inherit",
													display: "flex",
													justifyContent: "center",
													alignItems: "center",
													margin: "0.25rem ",
												}}
											>
												<WhatsAppIcon
													style={{
														color: "#FFFFFF",
														width: "2.5rem",
														height: "1.5rem",
														margin: "0.25rem 0rem 0.25rem 0rem",
													}}
												/>
											</Link>
										</div>
									)}
								</>
							)}
						</>
					) : null}
				</DialogActions>
			</CustomDialog>
			<Dialog
				open={openSingleViewModal}
				onClose={() => setOpenSingleViewModal(false)}
				maxWidth="xl"
				fullWidth
				TransitionComponent={DialogTransition}
				sx={{
					"& .MuiDialog-paper": {
						margin: 0,
						width: "100%",
						height: "100%",
						maxWidth: "100%",
						maxHeight: "100%",
						borderRadius: 0,
						backgroundColor: "rgba(0, 0, 0, 0.9)",
					},
				}}
			>
				<DialogTitle>
					<IconButton
						edge="end"
						// color="primary"
						onClick={() => setOpenSingleViewModal(false)}
						aria-label="close"
						sx={{
							position: "absolute",
							top: "10px",
							right: "20px",
							transform: "scale(1)",
							zIndex: 1,
							// backgroundColor: "transparent",
							color: "#FFFFFF",
						}}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>

				<DialogContent
					onTouchStart={onTouchStart}
					onTouchMove={onTouchMove}
					onTouchEnd={onTouchEnd}
					sx={{ textAlign: "center", padding: "0rem 0rem 2rem 0rem", overflow: "hidden" }}
				>
					{smDevice ? (
						<>
							{selectedImage && (
								<>
									<img
										src={selectedImage}
										alt="Selected Property Image"
										style={{ width: "100%", height: "100%", objectFit: "contain", position: "relative", zIndex: 0 }}
									/>
									{allImages.length > 1 && (
										<BottomArrowContainer>
											<IconButton
												onClick={handlePrevImage}
												sx={{
													color: "#FFFFFF",
												}}
											>
												<ArrowForwardIosIcon
													sx={{
														height: "2rem",
														width: "2rem",
														transform: "rotate(180deg)",
													}}
												/>
											</IconButton>
											<div
												style={{
													display: "flex",
													flexDirection: "column",
													justifyContent: "center",
													alignItems: "center",
												}}
											>
												{selectedImage && (
													<Typography sx={{ color: "#FFF", fontSize: "1rem", fontWeight: 400 }}>
														{Object.keys(allImageUrlsWithCategory)
															.find((category) =>
																allImageUrlsWithCategory[category as Category].includes(selectedImage),
															)
															?.replace(/_/g, " ")
															?.replace(/^\w/, (c) => c.toUpperCase())}
													</Typography>
												)}
												<Typography
													variant="body1"
													sx={{ color: "#FFF", fontSize: "1rem", fontWeight: 400 }}
												>
													{allImages.indexOf(selectedImage!) + 1} / {allImages.length}
												</Typography>
											</div>
											<IconButton
												onClick={handleNextImage}
												sx={{
													color: "#FFFFFF",
												}}
											>
												<ArrowForwardIosIcon
													sx={{
														height: "2rem",
														width: "2rem",
													}}
												/>
											</IconButton>
										</BottomArrowContainer>
									)}
								</>
							)}
						</>
					) : (
						<div
							style={{
								display: "flex",
								flexDirection: "row",
								justifyContent: "center",
								alignItems: "center",
								width: "100%",
								height: "100%",
							}}
						>
							<img
								src={selectedImage || ""}
								alt="Selected Property Image"
								style={{ width: "100%", height: "100%", objectFit: "contain", position: "relative", zIndex: 0 }}
							/>
							{allImages.length > 1 && (
								<IconButton
									onClick={handlePrevImage}
									sx={{
										color: "#FFFFFF",
										position: "absolute",
										top: "50%",
										left: "20px",
									}}
								>
									<ArrowForwardIosIcon
										sx={{
											height: "2rem",
											width: "2rem",
											transform: "rotate(180deg)",
										}}
									/>
								</IconButton>
							)}
							{allImages.length > 1 && (
								<IconButton
									onClick={handleNextImage}
									sx={{
										color: "#FFFFFF",
										position: "absolute",
										top: "50%",
										right: "20px",
									}}
								>
									<ArrowForwardIosIcon
										sx={{
											height: "2rem",
											width: "2rem",
										}}
									/>
								</IconButton>
							)}
						</div>
					)}
				</DialogContent>
			</Dialog>
		</React.Fragment>
	);
};

export default ViewPropertyImagesModal;
